import { useOrdersStore } from "./orders-store";
import api from "../../utils/api";
import { orderConsoleApi } from "../../utils/api";
import {
  OrderConsoleInitApiResponse,
  OrderConsoleDisplayApiResponse,
  OrderConsoleUpdateRequest,
  OrderStatusSetting,
  MerchantOrder
} from "../../types/order";

const { setState, getState } = useOrdersStore;

// Initialize order console (GET /ordering/console/init/{businessid})
export const initializeOrderConsole = async () => {
  const { businessId } = getState();
  setState({ isLoading: true });
  try {
    const response = await api.get(orderConsoleApi.init(businessId));
    let orderStatusSettings;
    if (response && Array.isArray(response.data)) {
      orderStatusSettings = response.data;
    } else if (response && response.data && response.data.statusSettings) {
      orderStatusSettings = response.data.statusSettings;
    } else {
      throw new Error("Invalid response from init API");
    }

    console.log(
      "🔧 Initializing order console with status settings:",
      orderStatusSettings
    );

    setState({
      orderStatusSettings: orderStatusSettings,
      lastDivId: `${
        orderStatusSettings[orderStatusSettings.length - 1].SettingId
      }_${(
        orderStatusSettings[orderStatusSettings.length - 1]
          .SettingName as string
      )
        .split(" ")
        .join("")}`,
      visibleViews: orderStatusSettings.map(
        (setting: any) =>
          `${setting.SettingId}_${(setting.SettingName as string)
            .split(" ")
            .join("")}`
      ),
      isLoading: false
    });

    // Call displayOrders after state is properly set
    setTimeout(() => {
      displayOrders();
    }, 100); // Small delay to ensure state is updated
  } catch (error) {
    console.error("Error initializing order console:", error);
    setState({
      isLoading: false,
      isErrorModalOpen: true,
      errorMessage:
        "YOU ARE NOT RECEIVING ORDERS\n\nThis is due to your tablet not being connected to the internet. Restart your tablet, modem or router. If problem persists, contact 1300 407 891."
    });
  }
};

// Display orders (GET /ordering/console/ordersinprogress/{businessid})
export const displayOrders = async () => {
  const { businessId, consoleRefreshCount, orderStatusSettings, audio } =
    getState();
  try {
    const response = await api.get(orderConsoleApi.display(businessId));
    let currentOrders;
    if (response && Array.isArray(response.data)) {
      currentOrders = response.data;
    } else if (
      response &&
      response.data &&
      Array.isArray(response.data.orders)
    ) {
      currentOrders = response.data.orders;
    } else {
      throw new Error("Invalid response from orders in progress API");
    }
    setState({ isErrorModalOpen: false });

    // Only process orders if orderStatusSettings is loaded
    if (currentOrders.length > 0 && orderStatusSettings.length > 0) {
      let playSound = false;
      let newOrdersAdded = false;
      const newOrdersMap = new Map(getState().orders); // Single map copy for all updates
      const originalOrders = getState().orders; // Keep reference to original state

      // Clear existing orders when we first get valid orders with loaded status settings
      // This ensures we start with a clean state when we're ready to process orders
      const hasExistingOrders = Array.from(originalOrders.values()).some(
        (orders) => orders.length > 0
      );
      if (hasExistingOrders) {
        newOrdersMap.clear();
      }

      // First pass: Add all orders immediately
      currentOrders.forEach((merchantOrder: MerchantOrder) => {
        const orderId = merchantOrder.orderId.toString();
        // Extract only the status part before any space
        let rawStatusId = merchantOrder.orderStatus;
        if (typeof rawStatusId === "string") {
          rawStatusId = rawStatusId.split(" ")[0];
        }
        let statusId = rawStatusId;

        // Try to find the matching key in orderStatusSettings
        const statusSetting = orderStatusSettings.find(
          (setting: OrderStatusSetting) => {
            const key = `${setting.SettingId}_${setting.SettingName.split(
              " "
            ).join("")}`;
            return (
              key === rawStatusId ||
              setting.SettingName === rawStatusId ||
              (typeof setting.SettingName === "string" &&
                typeof rawStatusId === "string" &&
                setting.SettingName.toLowerCase() === rawStatusId.toLowerCase())
            );
          }
        );
        if (statusSetting) {
          statusId = `${
            statusSetting.SettingId
          }_${statusSetting.SettingName.split(" ").join("")}`;
        } else {
          // Skip this order if we can't map it properly
          return;
        }

        // Check if this is a new order
        if (merchantOrder.IsNewOrder) {
          playSound = true;
        }

        const orderItem = {
          id: orderId,
          customerName: merchantOrder.FriendlyName,
          orderDetails: merchantOrder.merchantOrderDetails
            .map(
              (detail) =>
                `${detail.orderDetailQty} x ${detail.orderDetailItemName}`
            )
            .join("<br />"),
          buttonText: merchantOrder.orderButtonText,
          orderStatus: statusId,
          isProcessing: false
        };

        // Since we cleared the map, all orders should be treated as new
        const currentStatusOrders = newOrdersMap.get(statusId) || [];
        newOrdersMap.set(statusId, [orderItem, ...currentStatusOrders]);
        newOrdersAdded = true;
      });

      // Single state update for all changes
      if (newOrdersAdded) {
        setState({ orders: newOrdersMap });
      }

      // Play sound after state update for simultaneous audio and UI update
      if (playSound && audio && newOrdersAdded) {
        playNotificationSound();
      }

      // const newCount = consoleRefreshCount + 1;
      // setState({ consoleRefreshCount: newCount });
      // if (newCount >= 2) {
      //   updateMerchantConsole();
      // }
    }
    const newCount = consoleRefreshCount + 1;
    setState({ consoleRefreshCount: newCount });
    if (newCount >= 2) {
      updateMerchantConsole();
    }
  } catch (error: any) {
    console.error("Error fetching orders:", error);
    setState({
      isErrorModalOpen: true,
      errorMessage:
        "YOU ARE NOT RECEIVING ORDERS\n\nThis is due to your tablet not being connected to the internet. Restart your tablet, modem or router. If problem persists, contact 1300 407 891."
    });
  }
};

// Update order status (PUT /ordering/console/order/{orderId})
export const updateOrderStatus = async (
  orderId: string,
  currentStatus: string
) => {
  const { orderStatusSettings, lastDivId, businessId } = getState();
  setState({
    orders: new Map(getState().orders).set(
      currentStatus,
      (getState().orders.get(currentStatus) || []).map((order: any) =>
        order.id === orderId ? { ...order, isProcessing: true } : order
      )
    )
  });
  const currentStatusIndex = orderStatusSettings.findIndex(
    (setting: OrderStatusSetting) =>
      `${setting.SettingId}_${setting.SettingName.split(" ").join("")}` ===
      currentStatus
  );
  // Get current and next status info
  const currentSetting = orderStatusSettings[currentStatusIndex];
  const nextSetting = orderStatusSettings[currentStatusIndex + 1];
  // Clean status names
  const cleanCurrentName =
    currentSetting.SettingName.match(/^([A-Za-z]+)/)?.[1] ||
    currentSetting.SettingName;
  const cleanNextName =
    nextSetting?.SettingName.match(/^([A-Za-z]+)/)?.[1] ||
    nextSetting?.SettingName;
  // Compose OrderId and NextDivId (legacy ASP.NET compatible)
  const compoundOrderId = `${orderId}&${currentSetting.SettingId}_${cleanCurrentName}`;
  const nextStatus = nextSetting
    ? `${nextSetting.SettingId}_${cleanNextName}`
    : null;
  try {
    // Debug: Log the payload to verify structure
    console.log("Order status update payload:", {
      OrderId: compoundOrderId,
      NextDivId: nextStatus
    });
    await api.put(orderConsoleApi.updateStatus(businessId), {
      OrderId: compoundOrderId,
      ...(nextStatus && { NextDivId: nextStatus })
    });
    setState({ isErrorModalOpen: false });

    // Call displayOrders API again after successful status update to refresh the order list
    console.log("Order status updated successfully, refreshing orders...");
    await displayOrders();

    // Only update UI if API call succeeds
    if (currentStatus === lastDivId) {
      setState({
        orders: new Map(getState().orders).set(
          currentStatus,
          (getState().orders.get(currentStatus) || []).filter(
            (order: any) => order.id !== orderId
          )
        )
      });
    } else if (nextStatus) {
      const currentOrders = getState().orders.get(currentStatus) || [];
      const orderToMove = currentOrders.find(
        (order: any) => order.id === orderId
      );
      if (orderToMove) {
        const newOrders = new Map(getState().orders);
        newOrders.set(
          currentStatus,
          currentOrders.filter((order: any) => order.id !== orderId)
        );
        const nextStatusOrders = getState().orders.get(nextStatus) || [];
        // --- Button text logic fix: never show 'Next' ---
        let nextButtonText = "";
        const nextStatusName =
          orderStatusSettings[
            currentStatusIndex + 1
          ]?.SettingName?.toLowerCase() || "";
        if (nextStatusName.includes("done")) {
          nextButtonText = "Done";
        } else if (
          nextStatusName.includes("hide") ||
          nextStatusName.includes("completed")
        ) {
          nextButtonText = "Hide";
        } else if (nextStatusName.includes("accepted")) {
          nextButtonText = "Done";
        } else if (nextStatusName.includes("progress")) {
          nextButtonText = "Start";
        } else if (nextStatusName.includes("new")) {
          nextButtonText = "Accept";
        } else {
          nextButtonText = "Next";
        }
        // --- End button text logic fix ---
        nextStatusOrders.push({
          ...orderToMove,
          buttonText: nextButtonText,
          orderStatus: nextStatus,
          isProcessing: false
        });
        newOrders.set(nextStatus, nextStatusOrders);
        setState({ orders: newOrders });
      }
    }
  } catch (error: any) {
    setState({
      orders: new Map(getState().orders).set(
        currentStatus,
        (getState().orders.get(currentStatus) || []).map((order: any) =>
          order.id === orderId ? { ...order, isProcessing: false } : order
        )
      ),
      isErrorModalOpen: true,
      errorMessage:
        error?.message || String(error) || "Failed to update order status"
    });
  }
};

// Heartbeat (POST /ordering/console/heartbeat/{businessid})
export const updateMerchantConsole = async () => {
  const { businessId } = getState();
  try {
    await api.post(orderConsoleApi.heartbeat(businessId), {});
    setState({ consoleRefreshCount: 0 });
  } catch (error) {
    setState({ consoleRefreshCount: 3 });
  }
};

// Change view filter (unchanged)
export const changeView = (viewType: string) => {
  const { orderStatusSettings } = getState();
  if (viewType === "All") {
    setState({
      visibleViews: orderStatusSettings.map(
        (setting: OrderStatusSetting) =>
          `${setting.SettingId}_${setting.SettingName.split(" ").join("")}`
      ),
      activeView: "All"
    });
  } else {
    setState({
      visibleViews: [viewType],
      activeView: viewType
    });
  }
};

// Play notification sound (optimized for immediate playback)
export const playNotificationSound = () => {
  const { audio } = getState();
  if (!audio) {
    return;
  }
  try {
    // Reset audio to beginning and play immediately
    audio.currentTime = 0;
    audio.volume = 0.5;

    // Use Promise to ensure immediate playback
    const playPromise = audio.play();
    if (playPromise !== undefined) {
      playPromise.catch((error) => {
        console.error("Audio playback failed:", error);
      });
    }
  } catch (error) {
    console.error("Failed to play notification sound:", error);
  }
};

// Merchant logout with store reset
export const merchantLogout = async () => {
  try {
    // Clear the store before logout
    const { reset } = useOrdersStore.getState();
    reset();
    console.log("Store cleared before logout");
    window.location.href = "/login";
  } catch (error) {
    console.error("Error during logout:", error);
  }
};
